/**
 * Scoping and range arithmetic for /tasks/by-id.
 *
 * The two-field merge that used to live here moved into `useTaskIdHistory`
 * when the page went paged: interleaving two cursors is a property of the
 * fetch, not of a finished result set.
 */
import { toDate } from './taskCardModel';

/**
 * Milliseconds for ordering.
 *
 * Falls back through `uploadedAt` and `createdAt` so a capture sits at its
 * real position in the chronology rather than being dumped at the bottom
 * because one field is absent. Only a task with no date at all sorts last,
 * and it is still never dropped.
 */
function submittedMs(task) {
  const d = toDate(task?.submittedAt || task?.uploadedAt || task?.createdAt);
  return d ? d.getTime() : Number.NEGATIVE_INFINITY;
}

/**
 * Narrow to one customer. Both spellings are accepted for the same reason the
 * ID has two: writers disagree, and a reader that knows one sees nothing.
 * A falsy `customerId` means "do not scope" — the task we came from had no
 * customer, so there is nothing to scope to.
 */
export function scopeToCustomer(tasks, customerId) {
  if (!customerId) return tasks || [];
  return (tasks || []).filter(
    t => (t?.customerId || t?.customer_id) === customerId
  );
}

/** { first, last } Dates across the captures, or null if none are dated. */
export function captureSpan(tasks) {
  const times = (tasks || [])
    .map(submittedMs)
    .filter(ms => Number.isFinite(ms));
  if (times.length === 0) return null;
  return { first: new Date(Math.min(...times)), last: new Date(Math.max(...times)) };
}

/**
 * The /tasks/by-id address for one task ID.
 *
 * Query params rather than a path segment: operators type these, and a PO
 * number with a slash in it does not survive a path. `customerId` narrows the
 * page to one customer; without it every capture under the ID is listed.
 */
export function taskIdHistoryHref(value, customerId) {
  const params = new URLSearchParams({ value: String(value ?? '').trim() });
  if (customerId) params.set('customer', customerId);
  return `/tasks/by-id?${params.toString()}`;
}

/**
 * How many of a search's hits are tasks whose ID is exactly what was typed.
 *
 * An ID is often not unique: a forklift's number is the ID of its checklist
 * every day, so typing it found 83 tasks that all read "8397" and differed
 * only in the date. Two or more exact hits is the sign that the person wants
 * the ID's own page, where they are listed together in order.
 *
 * Case and surrounding spaces are ignored; a hit that only contains the query
 * ("18397") does not count.
 */
export function exactTaskIdMatches(results, query) {
  const wanted = String(query ?? '').trim().toLowerCase();
  if (!wanted) return 0;
  return (results || []).filter(
    r => r?.entity_type === 'task' && String(r.title ?? '').trim().toLowerCase() === wanted
  ).length;
}

/**
 * The ID to look up for what was typed.
 *
 * Firestore compares exactly, so "l748474" finds nothing where "L748474" has
 * a task. When a search hit's ID is the typed text in another case, that
 * hit's spelling is the one to ask for; otherwise the typed text as it is.
 */
export function taskIdToLookUp(results, query) {
  const typed = String(query ?? '').trim();
  const wanted = typed.toLowerCase();
  const hit = (results || []).find(
    r => r?.entity_type === 'task' && String(r.title ?? '').trim().toLowerCase() === wanted
  );
  return hit ? String(hit.title).trim() : typed;
}

/** The line under "All tasks with ID ...", from a count that may be unknown. */
export function sameIdSummary(exact) {
  if (exact === 1) return '1 task has exactly this ID.';
  if (exact > 1) return `${exact} tasks have exactly this ID. See them together, newest first.`;
  return 'Several tasks share this ID. See them together, newest first.';
}
